import React, { lazy, Suspense, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { WorkerAvatar } from './components/WorkerAvatar';
import { WorkerDetail } from './components/WorkerDetail';
import { StatsPanel } from './components/StatsPanel';
import { TelegramFeed } from './components/TelegramFeed';
import { PipelineView } from './components/PipelineView';
import { AssignHub } from './components/AssignHub';
import { TaskHistory } from './components/TaskHistory';
import { GitLabPanel } from './components/GitLabPanel';
import { WorkerChat } from './components/WorkerChat';
import { World3D } from './components/World3D';
import { AnalyticsPanel } from './components/AnalyticsPanel';
import { MiroPanel } from './components/MiroPanel';
import ArchitectWorkspace from './architect/ArchitectWorkspace';
import { peekLaunch } from './architect/launch';
import FeedbackPanel from './components/FeedbackPanel';
import RootWorkspace from './components/RootWorkspace';
import { useWebSocket } from './hooks/useWebSocket';
import { AIWorker } from './types';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AuthedUser } from './auth/api';
import { AuthRouter } from './auth/LoginPage';
import { SpendIndicator } from './components/SpendIndicator';
import TmaApp from './tma/TmaApp';
import { navigate, usePath } from './auth/router';
import { developersBase, navigateDevelopers } from './developers/developersRoutes';
import './App.css';

const DevelopersWorkspace = lazy(() => import('./developers/DevelopersWorkspace'));

// Lazy-loaded 3D scene — keeps three.js out of the base bundle
// const ThreeScene = lazy(() => import('./components/scene/ThreeScene'));

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:3001';

type Tab =
  | 'world'
  | /* 'sim' | */ 'team'
  | 'tasks'
  | 'analytics'
  | 'gitlab'
  | 'miro'
  | 'chat';

// Permissions that, on their own, do NOT grant access to the main
// admin surface. An account holding ONLY these lives in the confined
// shell (Architect / Feedback) — mirrors the backend's
// CONFINED_PERMISSIONS / hasMainAdminAccess split.
const CONFINED = new Set(['architect:use', 'architect:generate', 'architect:estimate', 'architect:pricing', 'feedback:submit', 'feedback:view']);
// Note: no perms.length guard — a zero-permission non-root account is
// confined too (the backend fail-closes it out of the main admin API
// and the WS), so it must land in the slim shell, not the Dashboard.
const isConfined = (u: AuthedUser, perms: string[]) =>
  !u.isRoot && perms.every((p) => CONFINED.has(p));

// Remembered per browser session so a page refresh doesn't re-ask.
// Values: 'developers' (robot workspace) | 'architect' (white poster
// dashboard) | 'root' (root admin surface — feedback, knowledge base,
// users). Each is its OWN full surface — they are not tabs of one
// dashboard. A stale 'feedback' value from an earlier build is mapped
// to 'root' on read.
const WORKSPACE_KEY = 'lato.workspace.v1';
type Workspace = 'developers' | 'architect' | 'root';

// Editorial section label — small-caps mono + numeric index + hairline
const SectionLabel: React.FC<{ index: string; title: string; caption?: string }> = ({ index, title, caption }) => (
  <div className="section-label">
    <span className="section-label-index">{index}</span>
    <span className="section-label-rule" />
    <span className="section-label-title">{title}</span>
    {caption && <span className="section-label-caption">— {caption}</span>}
  </div>
);

function Dashboard({ onOpenArchitect }: { onOpenArchitect?: () => void }) {
  const { workers, stats, notifications, phases, connected, refreshPhases } = useWebSocket(WS_URL);
  const { user, logout, hasPermission } = useAuth();
  const [selectedWorker, setSelectedWorker] = useState<AIWorker | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('world');

  const liveSelectedWorker = selectedWorker
    ? workers.find((w) => w.id === selectedWorker.id) || selectedWorker
    : null;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'world', label: 'Мир' },
    // { key: 'sim', label: 'Симуляция 3D' },
    { key: 'team', label: 'Команда' },
    { key: 'tasks', label: 'Задачи' },
    { key: 'analytics', label: 'Аналитика' },
    { key: 'gitlab', label: 'GitLab' },
    { key: 'miro', label: 'Miro' },
    { key: 'chat', label: 'Чат' },
  ];

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <motion.h1
            className="app-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Lato
          </motion.h1>
          <span className="app-subtitle">AI Virtual Development Team</span>
        </div>
        <div className="header-right">
          <div className="header-status">
            <span className={`status-dot ${connected ? 'status-online' : 'status-offline'}`} />
            <span>{connected ? 'Live' : 'Offline (mock)'}</span>
          </div>
          <span className="header-uptime">Uptime: {stats.uptime}</span>
          <SpendIndicator workers={workers} />
          {onOpenArchitect && hasPermission('architect:use') && (
            <button
              className="header-architect-link"
              onClick={onOpenArchitect}
              title="Architect — белый дашборд архитектурных постеров"
            >
              Architect ↗
            </button>
          )}
          {user && (
            <span className="user-chip" title={user.email}>
              <span className="user-chip-avatar">{(user.displayName || user.username).substring(0, 2)}</span>
              <span className="user-chip-name">{user.displayName || user.username}</span>
              {user.isRoot && <span className="user-chip-role">root</span>}
              <button className="user-chip-logout" onClick={logout} title="Выйти">× Выйти</button>
            </span>
          )}
        </div>
      </header>

      <StatsPanel stats={stats} />

      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`tab ${activeTab === t.key ? 'tab-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={`main-layout ${activeTab === 'chat' || activeTab === 'gitlab' || activeTab === 'world' || activeTab === 'analytics' || activeTab === 'miro' /* || activeTab === 'sim' */ ? 'layout-full' : ''}`}>
        <div className="main-content">
          {activeTab === 'world' && (
            <World3D workers={workers} phases={phases} onWorkerClick={setSelectedWorker} />
          )}
          {/* {activeTab === 'sim' && (
            <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading 3D scene…</div>}>
              <ThreeScene />
            </Suspense>
          )} */}
          {activeTab === 'team' && (
            <>
              <SectionLabel index="01" title="Roster" caption="Активная команда ИИ-воркеров" />
              <div className="workers-grid">
                {workers.map((worker) => (
                  <WorkerAvatar
                    key={worker.id}
                    worker={worker}
                    onClick={setSelectedWorker}
                    isSelected={selectedWorker?.id === worker.id}
                  />
                ))}
              </div>
              <div className="section-divider" />
              <SectionLabel index="02" title="Pipeline" caption="Фазы и модули в работе" />
              <PipelineView phases={phases} />
            </>
          )}

          {activeTab === 'tasks' && (
            <>
              <SectionLabel index="01" title="Assign" caption="Выбери агента → форма подстроится под его тип задач" />
              <AssignHub workers={workers} phases={phases} onRefresh={refreshPhases} />
              <div className="section-divider" />
              <SectionLabel index="02" title="Ledger" caption="История завершённых задач" />
              <TaskHistory />
            </>
          )}

          {activeTab === 'analytics' && <AnalyticsPanel />}
          {activeTab === 'gitlab' && <GitLabPanel />}
          {activeTab === 'miro' && <MiroPanel />}
          {activeTab === 'chat' && <WorkerChat workers={workers} />}
        </div>

        {(activeTab === 'team' || activeTab === 'tasks') && (
          <div className="sidebar">
            {liveSelectedWorker ? (
              <WorkerDetail
                worker={liveSelectedWorker}
                onClose={() => setSelectedWorker(null)}
              />
            ) : (
              <TelegramFeed notifications={notifications} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Slim dark surface for feedback-only accounts (Manager) and for the
// root "Фидбэк" workspace. The main dashboard's WebSocket and fetches
// (StatsPanel, SpendIndicator, /api/phases) 403/4403 for managers, so
// none of that mounts here.
function FeedbackShell({ onExit }: { onExit?: () => void }) {
  const { user, logout } = useAuth();
  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">Lato</h1>
          <span className="app-subtitle">Feedback — фидбэк по архитектурам</span>
        </div>
        <div className="header-right">
          {onExit && (
            <button className="header-architect-link" onClick={onExit} title="К выбору воркспейса">
              ← Воркспейсы
            </button>
          )}
          {user && (
            <span className="user-chip" title={user.email}>
              <span className="user-chip-avatar">{(user.displayName || user.username).substring(0, 2)}</span>
              <span className="user-chip-name">{user.displayName || user.username}</span>
              <button className="user-chip-logout" onClick={logout} title="Выйти">× Выйти</button>
            </span>
          )}
        </div>
      </header>

      <div className="main-layout layout-full">
        <div className="main-content">
          <FeedbackPanel />
        </div>
      </div>
    </div>
  );
}

// Root picks one of THREE separate surfaces after login. Each is its
// own full dashboard — nothing is folded into another as a tab.
function WorkspaceChooser({ onPick }: { onPick: (ws: Workspace) => void }) {
  return (
    <div className="auth-page">
      <div className="workspace-chooser">
        <h1 className="workspace-title">Lato</h1>
        <p className="workspace-caption">Куда идём?</p>
        <button className="workspace-card" onClick={() => onPick('developers')}>
          <span className="workspace-card-name">Developers</span>
          <span className="workspace-card-desc">Команда роботов, задачи, инструкции и память</span>
        </button>
        <button className="workspace-card" onClick={() => onPick('architect')}>
          <span className="workspace-card-name">Architect</span>
          <span className="workspace-card-desc">Белый дашборд — архитектурные постеры из заметок, редактор и PDF</span>
        </button>
        <button className="workspace-card" onClick={() => onPick('root')}>
          <span className="workspace-card-name">Root</span>
          <span className="workspace-card-desc">Фидбэк, база знаний оценщика, пользователи — админ-поверхность</span>
        </button>
      </div>
    </div>
  );
}

// Gate: show the login/bootstrap page until the user is authenticated.
// Then route to the right SURFACE:
//   architect account  → white Architect dashboard (no dark chrome)
//   manager account    → slim Feedback surface
//   developer          → robot workspace (can jump to Architect if granted)
//   root               → chooser of all three, switchable any time
function Gate() {
  const { user, permissions, loading, hasPermission, logout } = useAuth();
  const path = usePath();
  const [rememberedWorkspace, setWorkspaceState] = useState<Workspace | null>(() => {
    // DESIGN47 — a deep link into a project (from the mini app) lands in the
    // Architect workspace straight away, past root's chooser.
    if (peekLaunch()) return 'architect';
    const stored = sessionStorage.getItem(WORKSPACE_KEY);
    // Stale value from an earlier build — the manager surface used to be
    // a root workspace of its own; it now lives inside 'root'.
    return (stored === 'feedback' ? 'root' : stored) as Workspace | null;
  });
  const [enteredUser, setEnteredUser] = useState<AuthedUser | null>(null);
  const developerEntry = !!user && !user.isRoot && !isConfined(user, permissions);
  useEffect(() => {
    if (loading) return;
    if (!user) { setEnteredUser(null); return; }
    if (enteredUser === user) return;
    setEnteredUser(user);
    if (!developerEntry) return;
    // A newly authenticated developer starts in their own workspace.
    // A choice left by a previous account must not select Architect.
    setWorkspaceState('developers');
    try { sessionStorage.setItem(WORKSPACE_KEY, 'developers'); } catch { /* Routing still works without browser storage. */ }
    if (!developersBase(window.location.pathname)) navigateDevelopers({ view: 'fronts' }, true);
  }, [loading, user, enteredUser, developerEntry]);
  const workspace = developersBase(path) || path === '/operations' ? 'developers' : path === '/architect' ? 'architect' : path === '/root' ? 'root' : path === '/workspaces' ? null : rememberedWorkspace;
  const setWorkspace = (ws: Workspace | null) => {
    if (ws) sessionStorage.setItem(WORKSPACE_KEY, ws);
    else sessionStorage.removeItem(WORKSPACE_KEY);
    setWorkspaceState(ws);
    navigate(ws === 'developers' ? '/developers/projects' : ws === 'architect' ? '/architect' : ws === 'root' ? '/root' : '/workspaces');
  };

  if (loading) return <div className="auth-page"><div className="auth-loading">…</div></div>;
  if (!user) return <AuthRouter />;
  // Normalize the entry route before mounting either workspace or its sockets.
  if (developerEntry && enteredUser !== user) return <div className="developers-loading">Opening Developers…</div>;

  // Confined accounts go straight to their own surface — the backend
  // 403s the dashboard endpoints and closes the WS 4403 for them.
  if (isConfined(user, permissions)) {
    if (hasPermission('architect:use')) return <ArchitectWorkspace />;
    return <FeedbackShell />;
  }

  // Root: pick one of the three surfaces once per session.
  if (user.isRoot && !workspace) return <WorkspaceChooser onPick={setWorkspace} />;

  if (workspace === 'architect' && hasPermission('architect:use')) {
    return (
      <ArchitectWorkspace
        onExit={() => setWorkspace(user.isRoot ? null : 'developers')}
        exitLabel={user.isRoot ? '← Воркспейсы' : 'Developers ↗'}
      />
    );
  }
  if (workspace === 'root' && user.isRoot) {
    return <RootWorkspace onExit={() => setWorkspace(null)} />;
  }

  if (path !== '/operations') return <Suspense fallback={<div className="developers-loading">Loading the lab…</div>}>
    <DevelopersWorkspace key={user.id} userId={user.id} userName={user.displayName || user.username} canManageTeam={user.isRoot}
      onExit={user.isRoot ? () => setWorkspace(null) : undefined}
      onOpenArchitect={hasPermission('architect:use') ? () => setWorkspace('architect') : undefined}
      onLogout={() => { void logout(); }} />
  </Suspense>;

  return (
    <>
    <button className="devlab-return" onClick={() => navigate('/developers/projects')}>← Robot lab</button>
    <Dashboard
      onOpenArchitect={hasPermission('architect:use') ? () => setWorkspace('architect') : undefined}
    />
    </>
  );
}

export default function App() {
  const path = usePath();
  if (process.env.NODE_ENV === 'development' && developersBase(path) === '/developers-preview') {
    return <Suspense fallback={<div className="developers-loading">Loading the lab…</div>}><DevelopersWorkspace userId="design-preview" userName="Ameer"/></Suspense>;
  }
  // The Telegram Mini App is a separate surface served from the same CRA
  // bundle at /tma (DESIGN14 §F1.1). It owns its own auth (Telegram initData
  // → the same session JWT) and chrome, so it replaces the whole normal SPA
  // before any web-login UI mounts. Nothing else changes.
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/tma')
  ) {
    return <TmaApp />;
  }

  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
